import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, Sparkles, ShieldCheck } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { api, formatApiError } from "@/lib/api";

export default function Billing() {
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    Promise.all([api.get("/billing/plans"), api.get("/billing/me")])
      .then(([p, m]) => { setPlans(p.data); setStatus(m.data); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const subscribe = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout.");
      setBusy("");
    }
  };

  const manageBilling = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { plan_id: "business", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not open billing portal.");
      setBusy("");
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your plan? You'll keep access until the end of your current period, and you can re-subscribe anytime. No cancellation fees.")) return;
    setBusy("cancel");
    try {
      await api.post("/billing/cancel");
      toast.success("Canceled. Your plan is now Free — no further charges. Re-subscribe anytime.");
      load();
    } catch {
      toast.error("Could not cancel. Please try again or email support@wardensurveillance.com.");
    } finally {
      setBusy("");
    }
  };

  const isTrial = status?.plan_status === "trialing";
  const isActive = status?.plan_status === "active";
  const isCanceling = status?.plan_status === "canceling";
  const canCancel = status?.effective_plan && status.effective_plan !== "free";
  const hasSub = status?.has_subscription;

  return (
    <div className="min-h-screen text-white">
      <Navbar />
      <main className="pt-24 pb-20 max-w-5xl mx-auto px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyanbrand">Billing</p>
        <h1 className="font-heading text-3xl font-semibold mt-2">Plans & subscription</h1>

        {loading ? (
          <div className="mt-8 text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        ) : (
          <>
            <div className="mt-6 bg-[#0C1424] border border-slate-800 rounded-lg p-6 flex flex-wrap items-center justify-between gap-4" data-testid="billing-status">
              <div className="flex items-center gap-3">
                <ShieldCheck className="text-cyanbrand" size={26} />
                <div>
                  <p className="text-sm text-slate-400">Current plan</p>
                  <p className="font-heading text-xl font-semibold capitalize">
                    {status.effective_plan}
                    {isTrial && <span className="ml-2 text-sm text-cyanbrand">· Trial ({status.trial_days_left} days left)</span>}
                    {isActive && <span className="ml-2 text-sm text-green-400">· Active</span>}
                    {isCanceling && <span className="ml-2 text-sm text-amber-400">· Ends at period end</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {hasSub && (
                  <button
                    data-testid="manage-billing-button"
                    onClick={manageBilling}
                    disabled={busy === "portal"}
                    className="text-sm border border-cyanbrand/40 hover:border-cyanbrand text-cyanbrand px-4 py-2 rounded-md transition-colors"
                  >
                    {busy === "portal" ? "Opening…" : "Manage & cancel"}
                  </button>
                )}
                {canCancel && !hasSub && (
                  <button
                    data-testid="cancel-subscription-button"
                    onClick={cancel}
                    disabled={busy === "cancel"}
                    className="text-sm border border-slate-700 hover:border-red-500 text-slate-300 hover:text-red-400 px-4 py-2 rounded-md transition-colors"
                  >
                    {busy === "cancel" ? "Canceling…" : "Cancel plan"}
                  </button>
                )}
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-400 flex items-center gap-2" data-testid="cancel-anytime-note">
              <ShieldCheck size={15} className="text-green-400" />
              <span><span className="text-slate-200 font-medium">Cancel anytime</span> — no fees, no lock-in. You keep access until your period ends.</span>
            </p>
            {isTrial && (
              <p className="mt-3 text-sm text-slate-400 flex items-center gap-2">
                <Sparkles size={15} className="text-cyanbrand" />
                You're on a 7-day free trial with full Business features — no card required. Subscribe anytime to keep them.
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10 items-start">
              {plans.map((p) => {
                const current = isActive && status.plan === p.id;
                const popular = p.id === "business";
                return (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid={`billing-plan-${p.id}`}
                    className={`relative rounded-lg p-8 border transition-all ${
                      popular ? "bg-[#0C1424] border-cyanbrand shadow-[0_0_30px_rgba(0,229,255,0.15)]" : "bg-[#0C1424] border-slate-800"
                    }`}
                  >
                    {popular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cyanbrand text-slate-900 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                        Most Popular
                      </span>
                    )}
                    <h3 className="font-heading text-xl font-semibold">{p.name}</h3>
                    <div className="mt-4 flex items-baseline gap-1">
                      <span className="font-heading text-4xl font-bold">${p.price}</span>
                      <span className="text-slate-500">/mo</span>
                    </div>
                    <ul className="mt-6 space-y-3 text-sm text-slate-300">
                      <li className="flex items-center gap-2"><Check size={16} className="text-cyanbrand" /> {p.sites} website{p.sites > 1 ? "s" : ""}</li>
                      <li className="flex items-center gap-2"><Check size={16} className="text-cyanbrand" /> {p.frequency} scans</li>
                      <li className="flex items-center gap-2"><Check size={16} className="text-cyanbrand" /> AI reports + PDF export</li>
                      <li className="flex items-center gap-2"><Check size={16} className="text-cyanbrand" /> Instant alerts</li>
                      {p.id === "agency" && <li className="flex items-center gap-2"><Check size={16} className="text-cyanbrand" /> White-label + API</li>}
                    </ul>
                    <button
                      data-testid={`subscribe-${p.id}-button`}
                      disabled={current || busy === p.id}
                      onClick={() => subscribe(p.id)}
                      className={`mt-8 w-full py-3 rounded-md font-semibold transition-all disabled:opacity-60 ${
                        popular ? "bg-cyanbrand hover:bg-[#00B8D9] text-slate-900" : "border border-slate-700 hover:border-cyanbrand text-white"
                      }`}
                    >
                      {current ? "Current plan" : busy === p.id ? "Redirecting…" : "Subscribe"}
                    </button>
                  </motion.div>
                );
              })}
            </div>
            <p className="mt-6 text-xs text-slate-600">Payments processed securely by Stripe. Cancel anytime — no fees, no lock-in.</p>
          </>
        )}
      </main>
    </div>
  );
}
